// src/oura/components/onboarding/AnswersDrawer.tsx
//
// What the client answered on the onboarding form, for staff. Gaps come
// first, then one card per subject (business, customers, goals, people),
// each a column of label and answer rows. The client's words are shown as
// written; only the form's "1 - " numbering and pasted **bold** are tidied
// (answerText.ts).

import { useState, type ReactNode } from "react";
import { CheckIcon, FileTextIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import { ArrowLink, Drawer, DrawerNotice, ListRow, PillButton, SURFACE, TYPE } from "@oura/ui";
import type { Onboarding } from "@service/aeo/onboarding";
import { dayTime } from "../../onboarding/derive";
import { andList, askBlocks, boldParts, cleanOption, missingAnswers, missingForCheck } from "../../onboarding/answerText";

const MISSING = <span className="font-medium text-amber-700">Missing</span>;

function Card({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) {
    return (
        <section className={cn(SURFACE.block, "py-1")}>
            <div className="flex flex-wrap items-center justify-between gap-2 pb-1.5 pt-2.5">
                <h3 className={cn(TYPE.tb, "m-0 font-semibold text-gray-900")}>{title}</h3>
                {aside}
            </div>
            {children}
        </section>
    );
}

/** One answer: its label on the left (above it on a phone), the client's words on the right. */
function Row({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className={cn(TYPE.tb, "grid grid-cols-1 gap-x-4 gap-y-0.5 border-t py-3 sm:grid-cols-[148px_1fr]", SURFACE.hairline)}>
            <div className="text-gray-500">{label}</div>
            <div className="min-w-0 text-gray-900">{children}</div>
        </div>
    );
}

function Lines({ items, check }: { items: string[]; check?: boolean }) {
    if (items.length === 0) return MISSING;
    return (
        <ul className="list-plain m-0 space-y-1 p-0">
            {items.map((s) => (
                <li key={s} className={cn(check && "flex gap-2")}>
                    {check && <CheckIcon aria-hidden size={14} strokeWidth={2.5} className="mt-[3px] shrink-0 text-emerald-600" />}
                    <span>{s}</span>
                </li>
            ))}
        </ul>
    );
}

function Rich({ text }: { text: string }) {
    return <>{boldParts(text).map((p, i) => (p.bold ? <strong key={i} className="font-semibold">{p.text}</strong> : <span key={i}>{p.text}</span>))}</>;
}

function Asks({ items }: { items: string[] }) {
    const blocks = askBlocks(items);
    if (blocks.length === 0) return <div className={cn(TYPE.tb, "border-t py-3", SURFACE.hairline)}>{MISSING}</div>;
    let n = 0;
    return (
        <ol className="list-plain m-0 p-0">
            {blocks.map((b, i) => {
                if (b.kind === "text") {
                    return <li key={i} className={cn(TYPE.tb, "border-t py-3 text-gray-600", SURFACE.hairline)}><Rich text={b.text} /></li>;
                }
                n += 1;
                return (
                    <li key={i} className={cn(TYPE.tb, "flex gap-3 border-t py-3", SURFACE.hairline)}>
                        <span aria-hidden className="mt-px grid size-5 shrink-0 place-items-center rounded-full bg-gray-100 text-xs font-semibold text-gray-500">{n}</span>
                        <div className="min-w-0">
                            <p className="m-0 font-semibold text-gray-900">{b.question}</p>
                            {b.answer && <p className="m-0 mt-0.5 text-gray-600"><Rich text={b.answer} /></p>}
                        </div>
                    </li>
                );
            })}
        </ol>
    );
}

function Person({ name, email, noEmail }: { name: string; email?: string | null; noEmail?: string }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        if (!email) return;
        try {
            await navigator.clipboard.writeText(email);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch {
            // Clipboard refused (an insecure origin): the address is still there to select.
        }
    };
    return (
        <span className="flex flex-wrap items-baseline gap-x-2">
            <span>{name}</span>
            {email
                ? (
                    <>
                        <span className={cn(TYPE.tc, "break-all text-gray-500")}>{email}</span>
                        <button type="button" onClick={() => void copy()} className={cn(TYPE.tc, "font-medium text-blue-600 hover:underline")}>
                            {copied ? "Copied" : "Copy"}
                        </button>
                    </>
                )
                : noEmail && <span className={cn(TYPE.tc, "text-gray-500")}>{noEmail}</span>}
        </span>
    );
}

export function AnswersDrawer({ open, onboarding, knowledgeHref, onStartCheck, onClose }: {
    open: boolean;
    onboarding: Onboarding;
    knowledgeHref: string;
    /** Opens the first AI check, where staff can fill a gap the check needs. Absent once the check is not ours to start. */
    onStartCheck?: () => void;
    onClose: () => void;
}) {
    const a = onboarding.form.answers;
    if (!a) return null;
    const missing = missingAnswers(a);
    const forCheck = missingForCheck(a);
    const fixable = forCheck.length > 0 && onStartCheck;
    return (
        <Drawer
            open={open}
            onClose={onClose}
            title="Form answers"
            sub={`${onboarding.businessName} · answered ${dayTime(onboarding.form.answeredAt)} by ${a.contactName}`}
            width={600}
        >
            {missing.length > 0 && (
                <DrawerNotice action={fixable ? <PillButton variant="primary" size="sm" onClick={onStartCheck}>Add {forCheck.length === 1 ? forCheck[0].toLowerCase() : "them"}</PillButton> : undefined}>
                    <p className="m-0 font-semibold">{missing.length === 1 ? "1 answer missing" : `${missing.length} answers missing`}</p>
                    <p className="m-0 mt-0.5">
                        {forCheck.length > 0
                            ? `${andList(forCheck)} ${forCheck.length === 1 ? "is" : "are"} needed for the first AI check.`
                            : andList(missing)}
                    </p>
                </DrawerNotice>
            )}

            <Card title="Business" aside={<span className={cn(TYPE.tm, "rounded-lg bg-blue-50 px-2 py-0.5 font-medium text-blue-700")}>Used for the first AI check</span>}>
                <Row label="Services"><Lines items={a.services.map(cleanOption)} /></Row>
                <Row label="Outlet areas"><Lines items={a.outletAreas.map(cleanOption)} /></Row>
            </Card>

            <Card title="What customers ask" aside={<ArrowLink to={knowledgeHref}>Open in Knowledge</ArrowLink>}>
                <Asks items={a.customerQuestions} />
            </Card>

            <Card title="Goals and plans">
                <Row label="Goals"><Lines items={a.goals.map(cleanOption)} check /></Row>
                <Row label="Plans">{a.plans.trim() || <span className="text-gray-400">None given</span>}</Row>
                {a.other.trim() && <Row label="Anything else"><span className="whitespace-pre-line">{a.other}</span></Row>}
                {a.files.length > 0 && (
                    <Row label="Files">
                        {a.files.map((f) => (
                            <ListRow
                                key={f.name}
                                icon={<FileTextIcon aria-hidden size={16} className="text-[#FF3B30]" />}
                                title={f.url ? <a href={f.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{f.name}</a> : f.name}
                                meta={`${Math.max(1, Math.round(f.size / 1024))} KB`}
                            />
                        ))}
                    </Row>
                )}
            </Card>

            <Card title="People">
                <Row label="Filled in by"><Person name={a.contactName} email={a.contactEmail} /></Row>
                <Row label="Looks after access">
                    {a.accessContact ? <Person name={a.accessContact.name} email={a.accessContact.email} noEmail="No email given" /> : MISSING}
                </Row>
                <Row label="Approves changes">{a.approver.trim() || MISSING}</Row>
            </Card>
        </Drawer>
    );
}

// The same pieces lay out the onboarding form's notes in the Knowledge reader.
export { Card as AnswerCard, Row as AnswerRow, Lines as AnswerLines, Asks as AnswerAsks, Rich as AnswerRich };
